'use client';

import { preconnect } from 'react-dom';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';

import { MediaBusy } from 'src/components/cmui/media-busy';

import { videoOrigin } from '../utils/search-all-video-url';

/** 영상 아래 재생 바 높이(px) — 이 띠 위에서는 훑기를 멈춰 재생 바를 쓸 수 있게 한다 */
const CONTROLS_HEIGHT = 44;
/**
 * 주소를 **붙여 두는** 범위(px) — 화면에 곧 들어올 것만.
 *
 * 🚨 **넓히지 마라.** 이 범위 안의 영상은 길이를 모르면 머리말을 받으러 CDN 을 두드린다.
 *   격자에서는 그것이 수십 편이 되고, 브라우저 연결 한도(호스트당 6개)를 다 먹어 **정작
 *   훑고 있는 영상의 구간 요청이 그 뒤에 줄을 선다** — 훑기가 버벅이는 주된 이유다.
 * 🔑 넓게 잡을 이유였던 「길이를 미리 알아 두기」는 **색인이 대신한다**(`durationSec`) —
 *   `videosMeta` 에서 온 길이를 받으면 머리말 없이도 훑기가 선다.
 */
const KNOWN_MARGIN = 400;

/** 장면 이동이 이만큼(ms) 안 끝나면 「받는 중」을 보여 준다 — 더 짧으면 멀쩡한 이동에도 깜박인다 */
const SPINNER_DELAY = 140;

/** 훑을 수 있는 준비가 됐나 — 길이를 알고 그릴 프레임이 한 장은 있어야 한다 */
const HAVE_CURRENT_DATA = 2;

/**
 * 마우스가 다녀간 영상 중 **받은 것을 쥐고 있는** 편 수 — 지금 올라와 있는 한 편은 따로다.
 * 🔑 다시 올라오면 받아 둔 데이터로 곧바로 훑는다(「받는 중」이 다시 안 뜬다).
 * ⚠️ 늘리면 훑고 지나간 카드들이 그만큼 연결을 쥔다 — 연결 한도(호스트당 6개)를 지켜라.
 */
const RECENT_KEEP = 2;
/** 최근 다녀간 영상의 「떼어 내기」 — 오래된 것이 앞이다. 모든 `ScrubVideo` 가 함께 쓴다 */
const recentHovers: Array<() => void> = [];

/** 다녀간 영상을 맨 뒤에 올리고, `RECENT_KEEP` 을 넘는 오래된 것은 떼어 낸다 */
function rememberHover(park: () => void) {
  forgetHover(park);
  recentHovers.push(park);
  while (recentHovers.length > RECENT_KEEP) recentHovers.shift()?.();
}

function forgetHover(park: () => void) {
  const at = recentHovers.indexOf(park);
  if (at >= 0) recentHovers.splice(at, 1);
}

/**
 * 카드 폭을 몇 칸으로 끊어 훑을까 — **이동 횟수를 정하는 값**이다(규약 ④).
 *
 * 🔑 폭 300px 카드에서 1px 마다 걸면 이동이 300번이지만, 40칸이면 **최대 40번**이다.
 *   30초 영상이면 칸 하나가 0.75초라 키프레임 간격(보통 2~5초)보다 촘촘해 **보이는 장면은
 *   그대로**이고 헛일만 걷힌다.
 * ⚠️ 올리면 다시 버벅인다 — 이동 하나가 구간 요청 하나다.
 */
const SCRUB_STEPS = 40;

/**
 * 카드·상세의 영상 — 멈춰 있을 때 마우스를 좌우로 움직이면 그 위치의 장면으로 훑는다(빨간 세로선이 위치).
 * 재생 중이거나 아래 재생 바 위에서는 훑지 않는다. 프레임 이미지 없이 영상의 재생 위치만 바꾼다.
 *
 * ## 🚨 훑기가 버벅이지 않게 하는 규약 넷 — 하나만 빠져도 「마우스만 움직이고 그림은 안 따라오는」 화면이 된다
 * ① **한 그림 프레임에 한 번만** 움직인다. `mousemove` 는 초당 백 번 넘게 오는데 그때마다
 *    `currentTime` 을 쓰면 브라우저가 앞 이동을 취소하고 다시 시작해 **어느 것도 안 끝난다**.
 * ② **빨간 선을 리액트 상태로 옮기지 않는다.** 상태로 두면 마우스가 움직일 때마다 이 부품이 통째로
 *    다시 그려지고, 그 값이 `video` 의 속성까지 건드린다. 선은 **DOM 을 직접** 민다.
 * ③ **준비되기 전에는 훑지 않는다.** 길이도 모르고 프레임도 없는 영상에 이동을 걸면 아무 그림도 안 나오고
 *    요청만 쌓인다 — 그동안은 **「받는 중」 표시**를 주고 이동을 걸지 않는다.
 * ④ **자리를 칸으로 끊어 건다.** 1px 마다 새 이동을 걸면 카드 폭만큼(수백 번) 이동이 생기는데,
 *    영상 이동은 한 번마다 **구간 요청 + 키프레임부터 디코딩**이라 그 수를 감당하지 못한다.
 *    `SCRUB_STEPS` 칸으로 끊으면 이동 수가 한 자릿수 배로 줄고, **빨간 선은 여전히 마우스를
 *    그대로 따라가서** 손끝 느낌은 안 깎인다(선과 그림의 정밀도를 따로 둔다).
 *
 * ## 🚨 통째로 받는 것은 **마우스가 올라온 한 편뿐**이다
 * 「화면 가까이 온 영상」까지 `preload=auto` 로 두면 격자 한 줄이 통째로 내려받기에 들어간다 —
 * 실측 소셜포스팅 탭: 영상 14편 중 **10편이 동시에** 받고 있었고, 브라우저 연결 한도(호스트당 6개)를
 * 다 먹어 **이미지와 조회까지 굶었다**. 정작 훑고 있는 영상도 그래서 느려진다.
 * ⛔ 「가까우니까 미리 받자」로 되돌리지 마라 — 사람은 **한 번에 한 편만** 훑는다.
 *
 * 나머지는 주소만 붙여 **머리말(길이)을 받아 둔다** — 훑기는 `duration` 만 있으면 서고, 장면은
 * 그때그때 구간 요청으로 온다. 아주 멀어지면 주소를 떼어 받아 둔 데이터를 버린다(메모리).
 * 🔑 마우스가 다녀간 영상은 **최근 `RECENT_KEEP` 편만** 주소를 쥔다 — 그보다 오래된 것이 멈춰 있으면 주소를 떼어
 *   받기를 끊는다. `preload` 를 내려도 이미 시작한 받기는 멈추지 않아, 다 쥐고 있으면 훑고 지나간 카드들이 연결을
 *   계속 먹는다. 최근 것은 다시 올라와도 받아 둔 데이터로 곧바로 훑는다. 재생 중인 영상은 떼지 않는다.
 * 🔑 재생 바(`controls`)는 재생 중이거나 아래 띠에 마우스가 있을 때, 받아 둔 뒤에만 켠다 — 받는 중에 켜 두면 크롬이
 *   제 로딩 표시를 한가운데 크게 그려 `MediaBusy` 와 둘이 된다.
 * 🔑 마우스 아래에서 새로 붙은 영상(주소를 받는 동안 올라와 있던 카드)은 곧바로 「받는 중」을 이어 띄운다 —
 *   카드의 주소 받는 중 표시와 같은 자리·같은 모양(`MediaBusy`)이라 끊김 없이 이어진다.
 */
export function ScrubVideo({
  src,
  poster,
  durationSec,
  onSize,
  onFail,
  onSurfaceClick,
  alwaysControls = false,
}: {
  src: string;
  poster: string;
  /**
   * 색인이 아는 영상 길이(초) — 있으면 **머리말을 안 받고도** 훑기가 선다.
   * ⚠️ 없을 수도 있다(`videosMeta` 가 빈 글) — 그때는 영상이 스스로 알려 줄 때까지 기다린다.
   */
  durationSec?: number | null;
  /** 영상 고유 크기(`videoWidth`·`videoHeight`)를 알았을 때 — 칸 비율을 맞추는 쪽만 넘긴다 */
  onSize?: (w: number, h: number) => void;
  /** 재생 실패 — 부르는 쪽이 썸네일로 돌아간다 */
  onFail?: (reason: string) => void;
  /** 주면 재생 바 위쪽을 눌렀을 때 재생 대신 이것을 부른다(카드는 상세를 연다) — 재생은 재생 바의 버튼으로 한다 */
  onSurfaceClick?: () => void;
  /**
   * 재생 바를 받아 둔 뒤 늘 보인다 — 한 편만 크게 띄우는 상세 창만 켠다.
   * 🔑 켜면 그 한 편은 마우스를 기다리지 않고 받는다(`preload=auto`) — 격자에서는 켜지 마라(위 연결 한도).
   */
  alwaysControls?: boolean;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const ref = useRef<HTMLVideoElement | null>(null);
  const line = useRef<HTMLDivElement | null>(null);
  /**
   * 마지막 마우스 자리 — 그림 프레임마다 하나만 집어 간다(규약 ①).
   * 🔑 **집어 간 뒤에도 지우지 않는다** — 받기가 끝난 그 순간 커서가 있던 자리로 곧바로 따라가야 한다
   *   (안 지우면 「다 받았는데 마우스를 다시 흔들어야 움직이는」 화면이 된다).
   */
  const wanted = useRef<{ x: number; step: number } | null>(null);
  const frame = useRef(0);
  /** 이미 따라간 **칸** — 같은 칸을 다시 걸면 `seeked` 가 또 울려 프레임 고리가 끝나지 않는다 */
  const applied = useRef<number | null>(null);
  const seeking = useRef(false);
  const loaded = useRef(false);
  /** 첫 프레임을 한 번이라도 그렸나 — 그 뒤로는 이동이 늦어도 「받는 중」을 띄우지 않는다(주소를 떼면 다시 false) */
  const hasFrame = useRef(false);
  /** 주소를 붙여 둘 만큼 가까운가 — 길이를 미리 알아 두는 범위 */
  const [known, setKnown] = useState(false);
  /** 마우스가 올라와 있나 — **이 한 편만** 통째로 받는다 */
  const [hovered, setHovered] = useState(false);
  /** 다녀간 지 오래돼 받기를 끊었다 — 주소를 떼어 둔다. 다시 올라오면 푼다 */
  const [parked, setParked] = useState(false);
  /** 받는 중이라 아직 훑을 수 없다 — 「받는 중」 표시를 준다(규약 ③) */
  const [waiting, setWaiting] = useState(false);
  /** 마우스가 아래 재생 바 띠에 있나 — 이때와 재생 중에만 재생 바를 켠다 */
  const [inBand, setInBand] = useState(false);
  const inBandRef = useRef(false);
  /** 재생 중인가(재생 바로 튼 것) */
  const [playing, setPlaying] = useState(false);
  /** 막힘 없이 틀 만큼 받았나 — 아니면 재생 바를 끄고 `MediaBusy` 를 띄운다 */
  const [ready, setReady] = useState(false);
  const waitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** 최근 다녀간 목록에서 밀려났다 — 멈춰 있으면 주소를 뗀다(재생 중이면 그대로 둔다) */
  const park = useCallback(() => {
    if (ref.current?.paused ?? true) setParked(true);
  }, []);

  useEffect(() => () => forgetHover(park), [park]);

  // 마우스 아래에서 붙었다 — 받기를 곧바로 시작하고 「받는 중」을 이어 띄운다(첫 프레임이 오면 걷힌다)
  useEffect(() => {
    if (!box.current?.matches(':hover')) return;
    setHovered(true);
    loaded.current = true;
    setWaiting(true);
  }, []);

  useEffect(() => {
    const node = box.current;
    if (!node) return undefined;
    /** ⚠️ 관찰자는 **하나**다 — 영상마다 둘씩 달면 격자 하나에 수십 개가 돈다 */
    const observer = new IntersectionObserver(
      (entries) => setKnown(entries.some((e) => e.isIntersecting)),
      { rootMargin: `${KNOWN_MARGIN}px 0px` },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = ref.current;
    if (!video || (known && !parked)) return;
    // 멀어졌거나 다녀간 지 오래된 영상 — 렌더가 주소를 뗀 뒤 다시 불러야 받던 것을 끊고 버퍼를 놓는다
    if (loaded.current) {
      loaded.current = false;
      seeking.current = false;
      video.pause();
      video.load();
    }
  }, [known, parked]);

  /**
   * 🔑 가까이 온 영상의 CDN 에 **연결만** 미리 열어 둔다 — 마우스가 올라온 뒤 첫 요청이 DNS·TLS 를 기다리지 않는다.
   * 받는 바이트는 없다(`preload` 규약과 무관). 같은 오리진은 React 가 한 번만 건다.
   * ⚠️ `crossOrigin` 을 주지 마라 — `<video>` 는 no-cors 로 받으므로 연결 풀이 갈려 미리 연 연결을 못 쓴다.
   */
  useEffect(() => {
    const origin = known ? videoOrigin(src) : '';
    if (origin) preconnect(origin);
  }, [known, src]);

  /**
   * 「받는 중」 표시 — 곧바로 켜지 않는다(멀쩡한 이동에도 깜박인다).
   * 🔑 보여 줄 프레임이 하나도 없을 때만 켠다 — 첫 프레임 뒤의 늦은 이동은 마지막 프레임과 빨간 선으로 둔다
   *   (이동마다 도는 표시가 떴다 사라지면 화면 곳곳에 스피너가 깜박인다).
   */
  const armWaiting = useCallback(() => {
    if (waitTimer.current || hasFrame.current) return;
    waitTimer.current = setTimeout(() => {
      waitTimer.current = null;
      setWaiting(true);
    }, SPINNER_DELAY);
  }, []);

  const clearWaiting = useCallback(() => {
    if (waitTimer.current) {
      clearTimeout(waitTimer.current);
      waitTimer.current = null;
    }
    setWaiting(false);
  }, []);

  /**
   * 빨간 선은 DOM 을 직접 민다 — 상태로 두면 마우스가 움직일 때마다 부품이 통째로 다시 그려진다(규약 ②).
   * ⚠️ `left` 가 아니라 `transform` 으로 민다 — `left` 는 프레임마다 배치를 다시 재게 한다.
   */
  const moveLine = useCallback((x: number | null) => {
    const node = line.current;
    if (!node) return;
    node.style.display = x == null ? 'none' : 'block';
    if (x != null) node.style.transform = `translateX(${x}px)`;
  }, []);

  /** 그림 프레임마다 한 번 — 마지막 자리 하나만 따라간다 */
  const pump = useCallback(() => {
    frame.current = 0;
    const video = ref.current;
    const want = wanted.current;
    if (!video || !want) return;

    // 길이는 색인 것이 받쳐 준다 — 머리말이 아직 안 와도 훑기가 선다
    const span = Number.isFinite(video.duration) && video.duration > 0
      ? video.duration
      : Number(durationSec ?? 0);
    const canScrub = span > 0 && video.readyState >= HAVE_CURRENT_DATA;
    if (!canScrub) {
      // 아직 길이도 프레임도 없다 — 이동을 걸지 않는다. 걸어 봐야 요청만 쌓이고 그림은 안 나온다
      moveLine(null);
      armWaiting();
      return;
    }

    moveLine(want.x);
    // 이미 그 칸이다 — 다시 걸지 않는다(걸면 `seeked` 가 또 울려 프레임 고리가 끝나지 않는다)
    if (applied.current === want.step) {
      clearWaiting();
      return;
    }
    if (seeking.current) {
      // 앞 이동이 아직이다 — 이번 자리는 버린다(다음 프레임에 더 새로운 자리가 온다)
      armWaiting();
      return;
    }
    seeking.current = true;
    applied.current = want.step;
    armWaiting();
    /*
      🔑 **`fastSeek` 이 있으면 그것을 쓴다** — 키프레임으로만 가서 디코딩이 훨씬 적다.
        훑기는 「대충 그 언저리 장면」이면 되는 자리라 정확도를 내주고 속도를 얻는 것이 맞다.
      ⚠️ 크롬에는 없다 — 그때는 `currentTime` 으로 떨어진다(칸 끊기가 그쪽을 받쳐 준다).
    */
    const at = ((want.step + 0.5) / SCRUB_STEPS) * span;
    const fast = (video as HTMLVideoElement & { fastSeek?: (t: number) => void }).fastSeek;
    if (typeof fast === 'function') fast.call(video, at);
    else video.currentTime = at;
  }, [armWaiting, clearWaiting, moveLine, durationSec]);

  /** 재생 바 띠 안팎이 바뀔 때만 상태를 고친다 — 마우스 이동마다 다시 그리지 않게(규약 ②) */
  const markBand = useCallback((next: boolean) => {
    if (inBandRef.current === next) return;
    inBandRef.current = next;
    setInBand(next);
  }, []);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = ref.current;
    if (!video) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const band = e.clientY > rect.bottom - CONTROLS_HEIGHT;
    markBand(band);
    if (!video.paused) return;
    if (band) {
      wanted.current = null;
      moveLine(null);
      clearWaiting();
      return;
    }
    const width = rect.width || 1;
    const x = Math.min(width, Math.max(0, e.clientX - rect.left));
    // 선은 마우스 그대로, 이동은 칸으로 — 정밀도를 따로 둔다(규약 ④)
    wanted.current = {
      x,
      step: Math.min(SCRUB_STEPS - 1, Math.floor((x / width) * SCRUB_STEPS)),
    };
    if (!frame.current) frame.current = requestAnimationFrame(pump);
  };

  const stopScrub = useCallback(() => {
    wanted.current = null;
    applied.current = null;
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    }
    moveLine(null);
    clearWaiting();
  }, [clearWaiting, moveLine]);

  useEffect(() => () => {
    if (frame.current) cancelAnimationFrame(frame.current);
    if (waitTimer.current) clearTimeout(waitTimer.current);
  }, []);

  return (
    <Box
      ref={box}
      onMouseMove={handleMove}
      onMouseEnter={() => {
        setHovered(true);
        setParked(false);
        forgetHover(park);
        // 주소가 붙는 순간부터 받는다 — 머리말이 오기 전에 나가도 끊을 수 있게 표시해 둔다
        loaded.current = true;
      }}
      onMouseLeave={() => {
        setHovered(false);
        markBand(false);
        stopScrub();
        rememberHover(park);
      }}
      // 카드 안에서는 재생 바 조작이 카드 클릭(상세 열기)으로 번지지 않게 막는다
      onClick={onSurfaceClick ? (e) => e.stopPropagation() : undefined}
      sx={{ position: 'relative', width: 1, height: 1 }}
    >
      <Box
        component="video"
        ref={ref}
        src={known && !parked ? src : undefined}
        poster={poster || undefined}
        /*
          🚨 **재생 바는 재생 중이거나 아래 띠에 마우스가 있을 때만, 그것도 받아 둔 뒤에만 켠다.**
            `controls` 가 켜진 채 받는 중이면 크롬이 영상 한가운데에 제 로딩 표시를 크게 그린다 —
            작성자 CSS 로는 못 감춘다(그 패널은 크롬 내부 의사 요소다). 그러면 `MediaBusy` 와 도는 표시가 둘이 된다.
          받는 중 표시는 `MediaBusy` 하나다(아래).
        */
        controls={(alwaysControls || inBand || playing) && ready}
        playsInline
        muted
        /*
          🚨 **받는 것은 마우스가 올라온 한 편뿐이다.**
            길이를 아는 글은 그 밖에서 **아무것도 안 받는다**(`none`) — 머리말을 미리 받을
            이유가 색인 값으로 없어졌다. 길이를 모르는 글만 머리말을 받아 둔다.
          ⛔ 「가까우니까 미리 받자」로 되돌리지 마라 — 연결 한도를 먹어 훑기가 느려진다.
        */
        preload={(() => {
          if (hovered || alwaysControls) return 'auto';
          if (!known) return 'none';
          return Number(durationSec ?? 0) > 0 ? 'none' : 'metadata';
        })()}
        onLoadedMetadata={(e: React.SyntheticEvent<HTMLVideoElement>) => {
          loaded.current = true;
          onSize?.(e.currentTarget.videoWidth, e.currentTarget.videoHeight);
        }}
        // 그릴 프레임이 생겼다 — 여기서부터 훑기가 선다. 커서가 올라가 있으면 그 자리로 곧바로 따라간다
        onLoadedData={() => {
          hasFrame.current = true;
          clearWaiting();
          if (wanted.current && !frame.current) frame.current = requestAnimationFrame(pump);
        }}
        onSeeked={() => {
          seeking.current = false;
          clearWaiting();
          // 훑는 동안 마우스가 더 갔으면 그 자리로 이어 간다
          if (wanted.current && !frame.current) frame.current = requestAnimationFrame(pump);
        }}
        onPlay={() => {
          setPlaying(true);
          stopScrub();
        }}
        onPause={() => setPlaying(false)}
        onCanPlay={() => setReady(true)}
        onPlaying={() => setReady(true)}
        // 재생 중 끊겼다 — 재생 바를 끄고 `MediaBusy` 로 바꾼다. 멈춘 채 훑는 이동의 기다림은 `waiting` 이 맡는다
        onWaiting={(e: React.SyntheticEvent<HTMLVideoElement>) => {
          if (!e.currentTarget.paused) setReady(false);
        }}
        onEmptied={() => {
          hasFrame.current = false;
          setReady(false);
          setPlaying(false);
        }}
        onError={(e: React.SyntheticEvent<HTMLVideoElement>) => {
          seeking.current = false;
          clearWaiting();
          // 떼어 낸 주소(멀어진 영상)의 load() 도 error 를 낸다 — 주소가 붙어 있을 때만 실패로 본다
          if (e.currentTarget.getAttribute('src')) onFail?.(`MEDIA_ERR_${e.currentTarget.error?.code ?? 0}`);
        }}
        sx={{ width: 1, height: 1, objectFit: 'contain', display: 'block' }}
      />
      {onSurfaceClick && (
        <Box
          aria-label="상세 보기"
          role="button"
          onClick={onSurfaceClick}
          sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: CONTROLS_HEIGHT, cursor: 'pointer' }}
        />
      )}
      <Box
        ref={line}
        aria-hidden
        sx={{
          display: 'none',
          position: 'absolute',
          top: 0,
          bottom: `${CONTROLS_HEIGHT}px`,
          left: 0,
          width: 2,
          ml: '-1px',
          bgcolor: 'error.main',
          pointerEvents: 'none',
        }}
      />
      {/* 🔑 `hovered` 를 조건에 걸지 않는다 — 「받는 중」은 훑을 때(`pump`)·마우스 아래에서 붙었을 때·재생 바를 기다릴 때만
          켜지고, 마우스가 나가면 꺼진다(`stopScrub`). 올라와 있는지를 한 번 더 따지면 마우스 진입을 놓친 브라우저에서 표시가 통째로 사라진다 */}
      {(waiting || ((inBand || playing) && !ready)) && <MediaBusy label="장면을 받는 중" />}
    </Box>
  );
}
